import React, { useEffect, useState } from 'react'
import { Button, IconButton } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { toast } from 'react-toastify'
import moment from 'moment'
import { getAIProjectSettings, saveAIProjectSettings, testAIProjectConnection, syncDeepgram } from '../../repository/aiCostApi'
import '../../components/AICost/aiCost.css'

/** Password input with an eye toggle so the key can be checked while typing */
const SecretInput = ({ id, placeholder, value, onChange }) => {
  const [visible, setVisible] = useState(false)
  return (
    <div className="ai-integration-key ai-integration-secret">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        spellCheck={false}
        className="form-control"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <IconButton
        size="small"
        className="ai-integration-secret-toggle"
        aria-label={visible ? 'Hide key' : 'Show key'}
        title={visible ? 'Hide key' : 'Show key'}
        onClick={() => setVisible((v) => !v)}>
        {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </div>
  )
}

/**
 * Projects > Edit > AI Integration: the project's Deepgram API key + Deepgram Project ID,
 * and the Theratap x-api-key that syncs the project's Gemini usage. Keys are write-only - the backend stores it encrypted and only returns "••••1234".
 * The Project ID is optional (empty = the key's first project). A saved key is synced automatically.
 */
const AIIntegrationSettings = ({ projectId }) => {
  const [settings, setSettings] = useState(null)
  const [apiKey, setApiKey] = useState('')
  const [dgProjectId, setDgProjectId] = useState('')
  const [geminiKey, setGeminiKey] = useState('')
  const [geminiUrl, setGeminiUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [result, setResult] = useState(null)

  const applySettings = (data) => {
    setSettings(data)
    setDgProjectId(data?.deepgram?.deepgramProjectId || '')
    setGeminiKey(data?.gemini?.apiKeyValue || '')
    setGeminiUrl(data?.gemini?.baseUrl || '')
  }

  const load = async () => {
    try {
      const res = await getAIProjectSettings(projectId)
      applySettings(res.data)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  const save = async (body) => {
    setSaving(true)
    try {
      const res = await saveAIProjectSettings(projectId, body)
      applySettings(res.data)
      setApiKey('')
      setResult(null)
      toast.success(res.message)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    setTesting(true)
    try {
      const res = await testAIProjectConnection(projectId, {
        apiKey: apiKey || undefined,
        deepgramProjectId: dgProjectId.trim() || undefined,
      })
      const ok = res.data.canReadUsage && res.data.canReadBilling
      setResult({ type: ok ? 'success' : 'warning', message: res.message })
    } catch (err) {
      setResult({ type: 'danger', message: err.message })
    } finally {
      setTesting(false)
    }
  }

  const syncNow = async () => {
    setSyncing(true)
    try {
      const res = await syncDeepgram({ projectId })
      toast.success(res.data?.stats ? `Synced ${res.data.stats.requests} Deepgram requests` : res.message)
    } catch (err) {
      toast.error(err.message)
    }
    setSyncing(false)
    load()
  }

  if (loading) return <div className="ai-integration text-muted">Loading AI integration...</div>

  const dg = settings?.deepgram || {}
  const saved = dg.apiKey
  const idChanged = dgProjectId.trim().toLowerCase() !== (dg.deepgramProjectId || '')
  const gm = settings?.gemini || {}
  const deepgramChanged = !!apiKey.trim() || idChanged
  const geminiKeyChanged = geminiKey.trim() !== (gm.apiKeyValue || '')
  const geminiUrlChanged = geminiUrl.trim() !== (gm.baseUrl || '')
  const geminiChanged = geminiKeyChanged || geminiUrlChanged

  const saveAll = () => {
    const body = {}
    if (deepgramChanged) body.deepgram = { apiKey, deepgramProjectId: dgProjectId.trim() }
    if (geminiChanged) {
      body.gemini = {}
      // An emptied key field removes the saved key
      if (geminiKeyChanged) {
        if (geminiKey.trim()) body.gemini.apiKey = geminiKey.trim()
        else body.gemini.clearApiKey = true
      }
      if (geminiUrlChanged) body.gemini.baseUrl = geminiUrl.trim()
    }
    save(body)
  }

  return (
    <div className="ai-integration">
      <h5 className="mb-1">AI Integration</h5>
      <div className="text-muted small">The Deepgram key is stored encrypted and never shown again. Leave its field empty to keep the saved key.</div>

      <div className="ai-integration-provider">
        <label className="form-label mb-1" htmlFor="deepgram-key">
          <strong>Deepgram API key</strong>{' '}
          {saved?.configured && <span className="text-muted small">saved: {saved.masked}</span>}
        </label>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <SecretInput
            id="deepgram-key"
            placeholder={saved?.configured ? 'Enter a new key to replace it' : 'Deepgram API key (Admin or Owner role)'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <Button variant="outlined" size="small" disabled={testing || (!apiKey && !saved?.configured)} onClick={test}>
            {testing ? 'Testing...' : 'Test'}
          </Button>
          {saved?.configured && (
            <Button variant="outlined" size="small" disabled={syncing} onClick={syncNow}>
              {syncing ? 'Syncing...' : 'Sync now'}
            </Button>
          )}
          {saved?.configured && (
            <Button color="error" size="small" disabled={saving} onClick={() => save({ deepgram: { clearApiKey: true } })}>
              Remove
            </Button>
          )}
        </div>

        <label className="form-label mb-1 mt-3" htmlFor="deepgram-project-id">
          <strong>Deepgram Project ID</strong>{' '}
          <span className="text-muted small">optional - leave empty to use the key's first project</span>
        </label>
        <input
          id="deepgram-project-id"
          type="text"
          autoComplete="off"
          spellCheck={false}
          className="form-control ai-integration-key"
          placeholder="e.g. a96fc00b-5402-4859-ad2e-021dfcc87b3d"
          value={dgProjectId}
          onChange={(e) => setDgProjectId(e.target.value)}
        />

        {result && <div className={`alert alert-${result.type} py-2 mt-2 mb-0`}>{result.message}</div>}

        {dg.lastSyncAt && (
          <div className="ai-integration-status mt-2">
            Last sync {moment(dg.lastSyncAt).format('DD MMM YYYY, hh:mm A')}
            {dg.lastSyncError && <div className="text-danger">Last error: {dg.lastSyncError}</div>}
          </div>
        )}
      </div>

      <div className="ai-integration-provider">
        <label className="form-label mb-1" htmlFor="gemini-key">
          <strong>Gemini usage (Theratap x-api-key)</strong>
        </label>
        <input
          id="gemini-key"
          type="text"
          autoComplete="off"
          spellCheck={false}
          className="form-control ai-integration-key"
          placeholder="x-api-key from Theratap"
          value={geminiKey}
          onChange={(e) => setGeminiKey(e.target.value)}
        />

        <label className="form-label mb-1 mt-3" htmlFor="gemini-base-url">
          <strong>Theratap API base URL</strong>{' '}
          <span className="text-muted small">where the Gemini usage is pulled from</span>
        </label>
        <input
          id="gemini-base-url"
          type="url"
          autoComplete="off"
          spellCheck={false}
          className="form-control ai-integration-key"
          placeholder="e.g. https://api.theratap.com"
          value={geminiUrl}
          onChange={(e) => setGeminiUrl(e.target.value)}
        />

        {gm.syncEnabled === false && (
          <div className="ai-integration-status mt-2 text-warning">Sync is off: set the Theratap API base URL above and save.</div>
        )}
        {gm.lastSyncAt && (
          <div className="ai-integration-status mt-2">
            Last sync {moment(gm.lastSyncAt).format('DD MMM YYYY, hh:mm A')}
            {gm.lastSyncError && <div className="text-danger">Last error: {gm.lastSyncError}</div>}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="contained"
          disabled={saving || (!deepgramChanged && !geminiChanged)}
          onClick={saveAll}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  )
}

export default AIIntegrationSettings
